import type { CSSProperties, ReactElement } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  perceivedLuminance,
  readableFgOn,
  type DerivedPalette,
} from "../brand-colors";

const K = keys.quickSetup.logo.brandColors;
const STAR_SYMBOL = '✦';
const RIGHT_SYMBOL = '✓';

/**
 * BrandColorsPanel — presentational confirmation UI for the Logo step (Phase 3).
 *
 * Recreates `design_handoff_logo_colors/Logo Color Extraction.dc.html`:
 *  - uploaded-logo card (thumbnail tile + filename + "✓ Uploaded" + Replace)
 *  - gold brand-colors panel: header + hint, swatch row (pre-selected accent =
 *    large double-ring swatch with navy ✓ disc + "Accent"; others = hex-labeled),
 *    a desktop-only 170px mini storefront preview rendered from the DERIVED tokens
 *    (truthful — uses `readableFgOn` for on-color text so it matches the buyer),
 *    a "Use neutral colors instead" footer.
 *  - CTA whose label swaps to plain "Continue →" once neutral is chosen.
 *
 * Purely driven by props — no extraction / no network here (that lives in the
 * wizard wiring). All spacing/colors reuse `.qs-root` tokens; sizes + double-ring
 * live in `wizard.css` (`.qs-bc-*`) with logical properties for RTL.
 */
export function BrandColorsPanel({
  logo,
  filename,
  palette,
  selectedAccent,
  derived,
  theme,
  narrow,
  onSelectAccent,
  onNeutral,
  onReplace,
  onContinue,
}: {
  logo: string | null;
  filename: string;
  palette: string[];
  selectedAccent: string | null;
  derived: DerivedPalette | null;
  theme: "extracted" | "neutral";
  narrow: boolean;
  onSelectAccent: (hex: string) => void;
  onNeutral: () => void;
  onReplace: () => void;
  onContinue: () => void;
}): ReactElement {
  const t = useTranslations();
  const isNeutral = theme === "neutral";

  return (
    <div
      style={{
        animation: "qs-in .3s ease",
        display: "flex",
        flexDirection: "column",
        gap: narrow ? 18 : 24,
      }}
    >
      {/* ── Heading ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: narrow ? 12 : 14, alignItems: "flex-start" }}>
        <div
          style={{
            width: narrow ? 32 : 36,
            height: narrow ? 32 : 36,
            borderRadius: narrow ? 9 : 10,
            background: "var(--navy)",
            color: "var(--accent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: narrow ? 14 : 16,
            flexShrink: 0,
          }}
        >
          {STAR_SYMBOL}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <h2
            style={{
              margin: 0,
              fontSize: narrow ? 20 : 26,
              fontWeight: 800,
              color: "var(--navy)",
              letterSpacing: "-0.02em",
              lineHeight: 1.25,
            }}
          >
            {t(K.heading)}
          </h2>
          <p style={{ margin: 0, fontSize: narrow ? 13 : 15, color: "var(--muted)", lineHeight: 1.5 }}>
            {t(K.sub)}
          </p>
        </div>
      </div>

      {/* ── Uploaded-logo card ──────────────────────────────────── */}
      <div
        style={{
          border: "1.5px solid var(--border-strong)",
          borderRadius: narrow ? 14 : 16,
          background: "#fff",
          padding: narrow ? 14 : 20,
          display: "flex",
          alignItems: "center",
          gap: narrow ? 12 : 18,
        }}
      >
        <div
          style={{
            width: narrow ? 56 : 84,
            height: narrow ? 56 : 84,
            borderRadius: narrow ? 12 : 16,
            background: "var(--chip-bg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          {logo ? (
            <img
              src={logo}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                padding: narrow ? 8 : 12,
                boxSizing: "border-box",
              }}
            />
          ) : null}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: narrow ? 2 : 4, flex: 1, minWidth: 0 }}>
          <span
            style={{
              fontSize: narrow ? 13 : 15,
              fontWeight: 700,
              color: "var(--navy)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {filename || t(K.uploadedFile)}
          </span>
          <span style={{ fontSize: narrow ? 12 : 13, color: "var(--ok)", fontWeight: 600 }}>
            {t(K.uploadedStatus)}
          </span>
        </div>
        <button
          onClick={onReplace}
          style={{
            fontSize: narrow ? 12 : 13,
            fontWeight: 700,
            color: "var(--gold-ink)",
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            flexShrink: 0,
            fontFamily: "inherit",
          }}
        >
          {t(keys.quickSetup.logo.replace)}
        </button>
      </div>

      {/* ── Brand-colors panel / neutral confirmation ───────────── */}
      {isNeutral ? (
        <div
          style={{
            border: "1.5px solid var(--border-strong)",
            borderRadius: narrow ? 14 : 16,
            background: "var(--surface)",
            padding: narrow ? 16 : "18px 22px",
            fontSize: narrow ? 12.5 : 13,
            color: "var(--muted)",
            lineHeight: 1.5,
          }}
        >
          {t(K.neutralConfirm)}{" "}
          <button
            onClick={() => {
              const first = palette[0];
              if (first) onSelectAccent(first);
            }}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              fontWeight: 700,
              color: "var(--navy)",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {t(K.neutralToggleBack)}
          </button>
        </div>
      ) : (
        <div
          style={{
            border: "2px solid var(--accent)",
            borderRadius: narrow ? 14 : 16,
            background: "var(--sel-bg)",
            padding: narrow ? 16 : "20px 22px",
            display: "flex",
            flexDirection: "column",
            gap: narrow ? 14 : 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: narrow ? 11 : 12,
                fontWeight: 800,
                color: "var(--gold-ink)",
                letterSpacing: "0.08em",
              }}
            >
              {t(K.panelTitle)}
            </span>
            {!narrow && (
              <span style={{ fontSize: 13, color: "var(--muted)" }}>{t(K.panelHint)}</span>
            )}
          </div>

          <div style={{ display: "flex", gap: narrow ? 12 : 14, alignItems: "flex-end" }}>
            {palette.map((hex, i) => (
              <Swatch
                key={`${hex}-${i}`}
                hex={hex}
                selected={hex.toLowerCase() === (selectedAccent ?? "").toLowerCase()}
                narrow={narrow}
                index={i}
                accentLabel={t(K.accentLabel)}
                onSelect={onSelectAccent}
              />
            ))}

            {!narrow && derived && (
              <MiniPreview derived={derived} shopLabel={t(K.previewButton)} caption={t(K.previewCaption)} />
            )}
          </div>

          <span style={{ fontSize: narrow ? 12 : 13, color: "var(--muted)", lineHeight: 1.5 }}>
            {t(K.neutralPrefix)}{" "}
            <button
              onClick={onNeutral}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                fontWeight: 700,
                color: "var(--navy)",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {t(K.neutralCta)}
            </button>{" "}
            {t(K.neutralSuffix)}
          </span>
        </div>
      )}

      {/* ── CTA ─────────────────────────────────────────────────── */}
      <button
        onClick={onContinue}
        style={{
          height: narrow ? 52 : 54,
          border: "none",
          borderRadius: 10,
          background: "var(--accent)",
          color: "var(--navy)",
          fontSize: 16,
          fontWeight: 800,
          cursor: "pointer",
          fontFamily: "inherit",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isNeutral ? t(K.continueNeutral) : t(K.continueWith)}
      </button>
    </div>
  );
}

/** One color swatch — selected (accent) renders large with a double-ring + navy
 *  ✓ disc + "Accent" label; unselected renders smaller with a hex label. Light
 *  swatches get a 1px border so they don't vanish on the pale panel. */
function Swatch({
  hex,
  selected,
  narrow,
  index,
  accentLabel,
  onSelect,
}: {
  hex: string;
  selected: boolean;
  narrow: boolean;
  index: number;
  accentLabel: string;
  onSelect: (hex: string) => void;
}): ReactElement {
  // Perceived-luminance mirror of the buyer: bright swatches read as "light".
  const isLight = perceivedLuminance(hex) > 210;
  // The ✓ disc + checkmark color adapt so they stay visible on any accent.
  const discBg = perceivedLuminance(hex) > 150 ? "var(--navy)" : "#fff";
  const discFg = perceivedLuminance(hex) > 150 ? "var(--bg)" : "var(--navy)";

  const size = selected ? (narrow ? 54 : 64) : narrow ? 44 : 52;
  const radius = selected ? 14 : narrow ? 10 : 12;

  const box: CSSProperties = {
    width: size,
    height: size,
    borderRadius: radius,
    background: hex,
    border: isLight ? "1px solid var(--divider-2)" : "none",
    boxSizing: "border-box",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    flexShrink: 0,
  };
  if (selected) {
    // Double ring: sel-bg gap then navy outline (logical order — visual only).
    box.boxShadow = narrow
      ? "0 0 0 3px var(--sel-bg), 0 0 0 5px var(--navy)"
      : "0 0 0 3px var(--sel-bg), 0 0 0 5.5px var(--navy)";
    box.padding = narrow ? 3 : 4;
  }

  return (
    <div
      className={selected ? "qs-bc-swatch qs-bc-swatch-sel" : "qs-bc-swatch"}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: narrow ? 5 : 6,
        // Stagger pop-in (respects reduced-motion via wizard.css).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ["--qs-bc-delay" as any]: `${index * 80}ms`,
      }}
    >
      <button
        type="button"
        aria-pressed={selected}
        aria-label={selected ? accentLabel : hex}
        style={box}
        onClick={() => onSelect(hex)}
      >
        {selected && (
          <span
            style={{
              width: narrow ? 16 : 18,
              height: narrow ? 16 : 18,
              borderRadius: "50%",
              background: discBg,
              color: discFg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: narrow ? 9 : 10,
              fontWeight: 800,
            }}
          >
            {RIGHT_SYMBOL}
          </span>
        )}
      </button>
      {selected ? (
        <span style={{ fontSize: narrow ? 11 : 12, fontWeight: 800, color: "var(--navy)" }}>
          {accentLabel}
        </span>
      ) : (
        <span
          style={{
            fontSize: narrow ? 10 : 12,
            fontWeight: 600,
            color: "var(--muted)",
            fontFamily: "'JetBrains Mono', monospace",
            textTransform: "uppercase",
          }}
        >
          {hex}
        </span>
      )}
    </div>
  );
}

/** Desktop-only 170px mini storefront preview — truthful: header wears the ink,
 *  logo-chip + CTA wear the accent, page wears the surface, text lines wear the
 *  ink, and on-accent/on-header text is picked with the buyer's `readableFgOn`
 *  so the seller sees exactly what the storefront will render. */
function MiniPreview({
  derived,
  shopLabel,
  caption,
}: {
  derived: DerivedPalette;
  shopLabel: string;
  caption: string;
}): ReactElement {
  const { primary, accent, surface, ink } = derived;
  const onHeader = readableFgOn(ink);
  // The CTA is filled with `primary`, so its text must be readable against
  // `primary` — not `accent` — to match exactly what the buyer renders.
  const onPrimary = readableFgOn(primary);

  return (
    <div
      style={{
        marginInlineStart: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        alignItems: "flex-end",
      }}
    >
      <div
        style={{
          width: 170,
          border: "1px solid var(--gold-input)",
          borderRadius: 10,
          background: surface,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: 26,
            background: ink,
            display: "flex",
            alignItems: "center",
            padding: "0 8px",
            gap: 5,
          }}
        >
          <span style={{ width: 12, height: 12, borderRadius: 3, background: accent }} />
          <span
            style={{
              flex: 1,
              height: 5,
              borderRadius: 3,
              background: onHeader,
              opacity: 0.5,
              maxWidth: 44,
            }}
          />
        </div>
        <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ width: "70%", height: 6, borderRadius: 3, background: ink, opacity: 0.85 }} />
          <span style={{ width: "50%", height: 6, borderRadius: 3, background: ink, opacity: 0.55 }} />
          <span
            style={{
              alignSelf: "flex-start",
              padding: "3px 10px",
              borderRadius: 5,
              background: primary,
              color: onPrimary,
              fontSize: 9,
              fontWeight: 800,
              lineHeight: 1.4,
            }}
          >
            {shopLabel}
          </span>
        </div>
      </div>
      <span style={{ fontSize: 11, color: "var(--fine)" }}>{caption}</span>
    </div>
  );
}
